import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Users, Phone, Envelope, Hash, MapPin, Storefront,
    CalendarBlank, Plus, Lightning, CheckCircle, ArrowsClockwise,
    ChatCircleDots, Presentation, FileText, Receipt, Pencil,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import RoleGuard from "../../components/RoleGuard";
import OwnerCell from "../../components/OwnerCell";
import ReassignOwnerDialog from "../../components/ReassignOwnerDialog";
import EditBillingDetailsDialog from "../../components/EditBillingDetailsDialog";
import LeadLifecycleSections from "../../components/leads/LeadLifecycleSections";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

const LIFECYCLE = [
    "New", "Contacted", "Demo",
    // Iter 66 — POC, Requirement Gathering, Scope Freeze formalise the
    // presales discovery → commitment workflow.
    "POC", "Requirement Gathering", "Scope Freeze",
    "Proposal", "Won", "Lost",
];
const STATUS_TONE = {
    New: "bg-slate-50 text-slate-700 border-slate-200",
    Contacted: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Demo: "bg-violet-50 text-violet-700 border-violet-200",
    POC: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
    "Requirement Gathering": "bg-sky-50 text-sky-700 border-sky-200",
    "Scope Freeze": "bg-cyan-50 text-cyan-700 border-cyan-200",
    Proposal: "bg-amber-50 text-amber-700 border-amber-200",
    Won: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Lost: "bg-rose-50 text-rose-700 border-rose-200",
};

const DEMO_TYPES = [
    "Product Walkthrough", "Technical Deep-dive", "Commercial Discussion",
    "POC / Pilot Kickoff", "Training", "Other",
];
const DEMO_OUTCOMES = [
    "Positive", "Neutral", "Concerns Raised", "Follow-up Scheduled",
    "Pursuing Proposal", "Not Interested",
];

export default function LeadDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user } = useAuth();
    const isSuperAdmin = user?.role === "super_admin";
    const [lead, setLead] = useState(null);
    const [loading, setLoading] = useState(true);
    const [demoOpen, setDemoOpen] = useState(false);
    const [convertOpen, setConvertOpen] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    // Billing Details — operated against the Client master (the lead itself
    // never stores billing/tax fields per Iter 24 data principle).
    const [billingClient, setBillingClient] = useState(null);
    const [billingOpen, setBillingOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/leads/${id}`);
            setLead(data);
            // Look up an existing client linked to this lead — first by
            // provenance (sourced_from_lead_id), then by GSTIN match. This
            // keeps "Edit Billing Details" idempotent across multiple opens.
            try {
                let found = null;
                const list = await api.get("/billing/client/list");
                const items = list.data?.items || list.data || [];
                found = items.find((c) => c.sourced_from_lead_id === id) || null;
                if (!found && data.gstin) {
                    found = items.find((c) => (c.gstin || "").toUpperCase() === data.gstin.toUpperCase()) || null;
                }
                setBillingClient(found);
            } catch {
                // non-fatal — dialog will fall through to create-mode
                setBillingClient(null);
            }
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load lead"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const setStatus = async (status) => {
        try {
            await api.patch(`/leads/${id}/status`, { status });
            toast.success(`Status updated to ${status}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Status update failed"));
        }
    };

    if (loading && !lead) return <div data-testid="lead-detail-loading"><PageHeader eyebrow="Lead" title="Loading…" /></div>;
    if (!lead) return null;

    const stepIdx = LIFECYCLE.indexOf(lead.status);

    return (
        <div data-testid={`lead-detail-page-${lead.id}`}>
            <PageHeader
                eyebrow={
                    <Link to="/leads" className="inline-flex items-center gap-1 hover:text-slate-900 transition-colors">
                        <ArrowLeft size={12} /> Back to leads
                    </Link>
                }
                title={
                    <span className="flex items-center gap-3">
                        <Users size={22} weight="duotone" className="text-indigo-600" />
                        {lead.name}
                        <span className="text-slate-400 font-normal">·</span>
                        <span className="text-slate-600 font-normal">{lead.company}</span>
                    </span>
                }
                description={
                    <span className="inline-flex items-center gap-2 text-xs flex-wrap">
                        <span><span className="reedos-eyebrow">Source</span> <span className="ml-1">{lead.source || "—"}</span></span>
                        <span className="text-slate-300">·</span>
                        <span className="reedos-eyebrow">Owner</span>
                        <OwnerCell
                            owner={lead.owner}
                            testId="lead-detail-owner"
                            onReassignClick={isSuperAdmin ? () => setReassignOpen(true) : undefined}
                        />
                        <span className="text-slate-300">·</span>
                        <span><span className="reedos-eyebrow">Created</span> <span className="ml-1">{formatDate(lead.created_at)}</span></span>
                    </span>
                }
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" data-testid="lead-refresh-btn">
                            <ArrowsClockwise size={13} /> Refresh
                        </Button>
                        {lead.converted_proposal_id ? (
                            // Iter 66.10 — Lead has been converted. Replace
                            // the Convert button with a direct deep-link to
                            // the existing proposal so the operator can
                            // continue editing it from here.
                            <Button
                                size="sm"
                                onClick={() => nav(`/proposals/${lead.converted_proposal_id}`)}
                                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"
                                data-testid="lead-view-proposal-btn"
                            >
                                <Lightning size={13} weight="fill" /> View proposal →
                            </Button>
                        ) : (
                            // Iter 66.10 — Visible regardless of pipeline
                            // status (Won/Lost/Proposal). Status alone is
                            // not authoritative; only `converted_proposal_id`
                            // (set by the backend convert handler) is.
                            <RoleGuard permission="lead:write">
                                <Button
                                    size="sm"
                                    onClick={() => setConvertOpen(true)}
                                    className="gap-1.5 bg-amber-600 hover:bg-amber-700"
                                    data-testid="lead-convert-btn"
                                >
                                    <Lightning size={13} weight="fill" /> Convert to proposal
                                </Button>
                            </RoleGuard>
                        )}
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Lifecycle stepper */}
                <div className="reedos-card p-5" data-testid="lead-lifecycle-stepper">
                    <div className="reedos-eyebrow mb-3">Lifecycle</div>
                    <div className="flex items-center gap-1 overflow-x-auto">
                        {LIFECYCLE.slice(0, 4).map((st, idx) => (
                            <Step key={st} label={st} active={lead.status === st} complete={idx < stepIdx} testid={`lead-step-${st.toLowerCase()}`} />
                        ))}
                        {/* Terminal states shown only if active */}
                        {(lead.status === "Won" || lead.status === "Lost") && (
                            <Step key={lead.status} label={lead.status} active={true} complete={false} terminal testid={`lead-step-${lead.status.toLowerCase()}`} />
                        )}
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <RoleGuard permission="lead:write">
                            {LIFECYCLE.filter((s) => s !== lead.status).map((s) => (
                                <Button
                                    key={s}
                                    size="sm"
                                    variant={s === "Won" ? "default" : s === "Lost" ? "outline" : "ghost"}
                                    onClick={() => setStatus(s)}
                                    className={classJoin(
                                        "h-7 text-[11px] px-2 gap-1",
                                        s === "Won" && "bg-emerald-600 hover:bg-emerald-700",
                                        s === "Lost" && "border-rose-300 text-rose-700 hover:bg-rose-50",
                                    )}
                                    data-testid={`lead-set-${s.toLowerCase()}`}
                                    title={s === "Proposal"
                                        ? "Marks the pipeline stage only. To actually create a proposal, click 'Convert to proposal' in the page header."
                                        : undefined}
                                >
                                    {s === "Proposal" ? "Mark stage: Proposal" : `Move to ${s}`}
                                </Button>
                            ))}
                        </RoleGuard>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {/* Info */}
                    <div className="reedos-card p-5 lg:col-span-1 space-y-3" data-testid="lead-info-card">
                        {/* Billing Details — Iter 24 — single source of truth is the linked Client */}
                        <div className="rounded-md border border-indigo-200 bg-indigo-50/40 px-3.5 py-3 -mx-1 -mt-1 mb-1" data-testid="lead-billing-block">
                            <div className="flex items-center justify-between gap-2 mb-2">
                                <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px] text-indigo-700">
                                    <Receipt size={11} weight="duotone" /> Billing &amp; Tax
                                </div>
                                <RoleGuard permission="client:write">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => setBillingOpen(true)}
                                        className="h-6 px-2 text-[10px] gap-1 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                                        data-testid="lead-edit-billing-btn"
                                    >
                                        <Pencil size={10} /> {billingClient ? "Edit" : "Capture"}
                                    </Button>
                                </RoleGuard>
                            </div>
                            {billingClient ? (
                                <div className="space-y-1 text-[11px]">
                                    <div className="font-semibold text-slate-900">{billingClient.legal_name || billingClient.name}</div>
                                    {billingClient.gstin && (
                                        <div className="font-mono text-slate-600">GSTIN {billingClient.gstin}</div>
                                    )}
                                    {(billingClient.address_line1 || billingClient.address) && (
                                        <div className="text-slate-600 leading-tight">
                                            {billingClient.address_line1 || billingClient.address}
                                            {billingClient.city && <>, {billingClient.city}</>}
                                            {billingClient.state_name && <> · {billingClient.state_name}</>}
                                            {billingClient.pin_code && <> {billingClient.pin_code}</>}
                                        </div>
                                    )}
                                    {(billingClient.place_of_supply || billingClient.gst_type) && (
                                        <div className="text-[10px] text-slate-500 mt-1">
                                            {billingClient.place_of_supply && <>POS: <strong>{billingClient.place_of_supply}</strong></>}
                                            {billingClient.gst_type && <> · {billingClient.gst_type}</>}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-[11px] text-slate-600 italic">
                                    No billing client linked yet — capture the GSTIN, address, place of supply &amp; GST type so they snapshot onto the proposal automatically.
                                </div>
                            )}
                        </div>

                        <InfoRow icon={<Users size={12} />} label="Contact" value={lead.name} />
                        <InfoRow icon={<Storefront size={12} />} label="Company" value={lead.company} />
                        {lead.contact_email && <InfoRow icon={<Envelope size={12} />} label="Email" value={lead.contact_email} mono />}
                        {lead.contact_phone && <InfoRow icon={<Phone size={12} />} label="Phone" value={lead.contact_phone} mono />}
                        {lead.gstin && <InfoRow icon={<Hash size={12} />} label="GSTIN" value={lead.gstin} mono />}
                        {lead.state_code && <InfoRow icon={<MapPin size={12} />} label="State · GST" value={`${lead.state_code} · ${lead.gst_type || "—"}`} mono />}
                        {(lead.address_line1 || lead.city || lead.pin_code) && (
                            <InfoRow
                                icon={<MapPin size={12} />}
                                label="Billing address"
                                value={[lead.address_line1, lead.city, lead.pin_code].filter(Boolean).join(", ")}
                            />
                        )}
                        {lead.segment && <InfoRow icon={<FileText size={12} />} label="Segment" value={lead.segment} />}
                        {lead.notes && (
                            <div>
                                <div className="reedos-eyebrow flex items-center gap-1.5"><ChatCircleDots size={11} /> Notes</div>
                                <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{lead.notes}</div>
                            </div>
                        )}
                    </div>

                    {/* Demos */}
                    <div className="reedos-card overflow-hidden lg:col-span-2" data-testid="lead-demos-card">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                            <div className="reedos-eyebrow flex items-center gap-1.5"><Presentation size={11} /> Demos ({lead.demos.length})</div>
                            <RoleGuard permission="lead:write">
                                <Button size="sm" variant="outline" onClick={() => setDemoOpen(true)} className="h-7 text-[11px] gap-1" data-testid="lead-demo-add-btn">
                                    <Plus size={11} /> Log demo
                                </Button>
                            </RoleGuard>
                        </div>
                        {lead.demos.length === 0 ? (
                            <EmptyState icon={<Presentation size={32} weight="thin" />} title="No demos logged" subtitle="Log the first conversation to progress this lead." />
                        ) : (
                            <div className="divide-y divide-slate-100">
                                {lead.demos.map((d) => (
                                    <div key={d.id} className="px-5 py-3" data-testid={`lead-demo-row-${d.id}`}>
                                        <div className="flex items-baseline justify-between">
                                            <div className="text-sm font-semibold text-slate-900">{d.demo_type}</div>
                                            <div className="text-[10px] text-slate-500 font-mono">{formatDate(d.date)}</div>
                                        </div>
                                        <div className="text-[11px] text-slate-600 mt-0.5">
                                            Outcome: <span className="font-semibold">{d.outcome}</span>
                                            {d.attendees && <span> · Attendees: {d.attendees}</span>}
                                        </div>
                                        {d.notes && <div className="text-[11px] text-slate-500 mt-1 whitespace-pre-wrap">{d.notes}</div>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Iter 66 — Sales lifecycle sub-documents: Requirement Capture,
                    Scope Definition, Proposal Readiness. Cards drive presales
                    discovery → commercial commitment, and the snapshot mechanism
                    locks the agreed scope onto the proposal at conversion. */}
                <RoleGuard permission="lead:write">
                    <LeadLifecycleSections lead={lead} onChanged={load} />
                </RoleGuard>

                {/* Linked proposals */}
                <div className="reedos-card overflow-hidden" data-testid="lead-proposals-card">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
                        <div className="reedos-eyebrow flex items-center gap-1.5"><FileText size={11} /> Linked proposals ({lead.proposals.length})</div>
                    </div>
                    {lead.proposals.length === 0 ? (
                        <EmptyState
                            icon={<FileText size={32} weight="thin" />}
                            title="No proposals linked yet"
                            subtitle="Convert this lead to a proposal to start the commercial flow."
                        />
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {lead.proposals.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/proposals/${p.id}`}
                                    className="px-5 py-3 block reedos-row-hover"
                                    data-testid={`lead-proposal-row-${p.id}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <div className="font-mono font-semibold text-slate-900 text-sm">{p.proposal_number}</div>
                                            <div className="text-[11px] text-slate-600">{p.kind_attention || "—"} · {p.module_code || "—"}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={classJoin("inline-flex rounded px-2 py-0.5 text-[10px] font-semibold border", STATUS_TONE[p.status] || STATUS_TONE.New)}>
                                                {p.status}
                                            </span>
                                            <div className="text-[10px] text-slate-500">{formatDate(p.created_date)}</div>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <DemoDialog
                open={demoOpen}
                onClose={() => setDemoOpen(false)}
                leadId={lead.id}
                onCreated={() => { setDemoOpen(false); load(); }}
            />
            <ConvertDialog
                open={convertOpen}
                onClose={() => setConvertOpen(false)}
                lead={lead}
                onConverted={(proposal_id) => nav(`/proposals/${proposal_id}`)}
            />
            <ReassignOwnerDialog
                open={reassignOpen}
                onOpenChange={setReassignOpen}
                entityLabel="Lead"
                entityName={lead.company || lead.name}
                currentOwner={lead.owner}
                reassignUrl={`/leads/${id}/owner`}
                onSuccess={load}
                testIdPrefix="lead-detail-reassign"
            />
            <EditBillingDetailsDialog
                open={billingOpen}
                onOpenChange={setBillingOpen}
                client={billingClient}
                leadId={!billingClient ? id : null}
                seed={!billingClient ? {
                    name: lead.company || "",
                    legal_name: lead.company || "",
                    gstin: lead.gstin || "",
                    state_code: lead.state_code || "",
                    contact_name: lead.name || "",
                    contact_email: lead.contact_email || "",
                    contact_phone: lead.contact_phone || "",
                    billing_contact_name: lead.name || "",
                    billing_email: lead.contact_email || "",
                    billing_phone: lead.contact_phone || "",
                    segment: lead.segment || "",
                } : null}
                onSaved={load}
            />
        </div>
    );
}

function Step({ label, active, complete, terminal, testid }) {
    return (
        <>
            <div
                className={classJoin(
                    "flex-1 h-8 rounded flex items-center justify-center text-[11px] font-semibold transition-colors min-w-[80px]",
                    active ? (terminal
                        ? (label === "Won" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white")
                        : "bg-indigo-600 text-white")
                        : complete ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"
                )}
                data-testid={testid}
            >
                {complete && <CheckCircle size={11} weight="fill" className="mr-1" />}
                {label}
            </div>
            {!terminal && <CaretRightSeparator />}
        </>
    );
}

function CaretRightSeparator() {
    return <div className="text-slate-300 px-1 text-xs">›</div>;
}

function InfoRow({ icon, label, value, mono }) {
    return (
        <div className="flex items-start gap-2 text-xs">
            <span className="text-slate-400 mt-0.5">{icon}</span>
            <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
                <div className={classJoin("text-slate-900 mt-0.5 truncate", mono && "font-mono")}>{value}</div>
            </div>
        </div>
    );
}

function DemoDialog({ open, onClose, leadId, onCreated }) {
    const blank = () => ({
        date: new Date().toISOString().slice(0, 10),
        demo_type: "Product Walkthrough",
        outcome: "Positive",
        attendees: "",
        notes: "",
    });
    const [form, setForm] = useState(blank);
    const [busy, setBusy] = useState(false);
    const baselineRef = useRef(JSON.stringify(blank()));

    useEffect(() => {
        if (open) {
            const next = blank();
            setForm(next);
            baselineRef.current = JSON.stringify(next);
        }
        // eslint-disable-next-line
    }, [open]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const submit = async () => {
        setBusy(true);
        try {
            await api.post(`/leads/${leadId}/demos/create`, form);
            toast.success("Demo logged");
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to log demo"));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <FormDialog
            open={open}
            onOpenChange={(o) => { if (!o) onClose(); }}
            isDirty={isDirty}
            contentClassName="sm:max-w-md"
            testId="demo-dialog"
            title={<span className="flex items-center gap-2"><Presentation size={18} weight="duotone" className="text-violet-600" />Log demo</span>}
            description='Logging a demo auto-moves the lead to "Demo" stage.'
        >
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Date</Label>
                            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 h-9" data-testid="demo-field-date" />
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Type</Label>
                            <select value={form.demo_type} onChange={(e) => setForm({ ...form, demo_type: e.target.value })} className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="demo-field-type">
                                {DEMO_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Outcome</Label>
                        <select value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="demo-field-outcome">
                            {DEMO_OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Attendees</Label>
                        <Input value={form.attendees} onChange={(e) => setForm({ ...form, attendees: e.target.value })} placeholder="Names + roles" className="mt-1 h-9 text-sm" data-testid="demo-field-attendees" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Notes</Label>
                        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded resize-none" data-testid="demo-field-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <FormDialogCancel variant="ghost" testId="demo-cancel">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={busy} data-testid="demo-submit" className="gap-1.5">
                        <CalendarBlank size={13} /> {busy ? "Saving…" : "Log demo"}
                    </Button>
                </DialogFooter>
        </FormDialog>
    );
}

function ConvertDialog({ open, onClose, lead, onConverted }) {
    // Iter 66.5 — Convert dialog mirrors the admin's "New from Template"
    // dialog so Sales can hydrate a proposal end-to-end in one shot:
    // template_id (header + line items auto-populated), currency,
    // kind_attention, proposal_number override, header_text, remarks.
    // Iter 66.12 — module_code field removed; backend auto-derives.
    const [form, setForm] = useState({
        template_id: "",
        header_text: "",
        kind_attention: "",
        proposal_number: "",
        currency: "INR",
        remarks: "",
    });
    const [templates, setTemplates] = useState([]);
    const [busy, setBusy] = useState(false);
    const baselineRef = useRef("");

    useEffect(() => {
        if (!open) return;
        const next = {
            template_id: "",
            header_text: `Proposal for ${lead?.company || ""}`,
            kind_attention: lead?.name || "",
            proposal_number: "",
            currency: "INR",
            remarks: "",
        };
        setForm(next);
        baselineRef.current = JSON.stringify(next);
        // Iter 66.12 — only fetch templates now; modules no longer needed
        // since the field has been removed from the dialog (backend
        // auto-derives from the chosen template).
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/billing/proposal-template/list", { params: { is_active: true } });
                if (!cancelled) setTemplates((data?.items || []).filter((x) => x.is_active !== false));
            } catch {
                if (!cancelled) setTemplates([]);
            }
        })();
        return () => { cancelled = true; };
    }, [open, lead]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    // When a template is picked, hydrate header_text from the template so the
    // operator gets the visual feedback before submit. The backend will also
    // hydrate line items from the same template_id at create time.
    const onTemplateChange = (tplId) => {
        const tpl = templates.find((t) => t.id === tplId);
        setForm({
            ...form,
            template_id: tplId,
            header_text: tpl?.header_text || form.header_text,
        });
    };

    const submit = async () => {
        setBusy(true);
        try {
            const payload = {
                template_id: form.template_id || null,
                header_text: form.header_text,
                kind_attention: form.kind_attention,
                proposal_number: form.proposal_number || null,
                currency: form.currency || "INR",
                remarks: form.remarks,
            };
            const { data } = await api.post(`/leads/${lead.id}/convert-to-proposal`, payload);
            toast.success(`Proposal ${data.proposal_number} created`, {
                description: form.template_id
                    ? "Header + line items hydrated from template — add more on the proposal page."
                    : "Add line items, T&C, and customisations on the proposal detail page.",
            });
            // Iter 67.3 — Surface soft-advisory warnings (e.g. converted
            // without signed Proposal Readiness) as a separate non-blocking
            // toast so the operator knows but doesn't get stopped.
            const warnings = Array.isArray(data.warnings) ? data.warnings : [];
            for (const w of warnings) {
                toast.warning(w, { duration: 8000 });
            }
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            onConverted(data.proposal_id);
        } catch (err) {
            // Iter 66.10 — Handle LEAD_ALREADY_CONVERTED gracefully: the
            // backend returns a structured payload with the existing
            // proposal id. Surface it as a toast + auto-navigate so the
            // operator never gets stuck with a generic 409 message.
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 409 && detail && detail.code === "LEAD_ALREADY_CONVERTED") {
                toast.info(`Lead already converted to ${detail.proposal_number || detail.proposal_id}`, {
                    description: "Opening the existing proposal now.",
                });
                baselineRef.current = JSON.stringify(form);
                onConverted(detail.proposal_id);
                return;
            }
            toast.error(formatApiError(err, "Conversion failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!open || !lead) return null;
    return (
        <FormDialog
            open={open}
            onOpenChange={(o) => { if (!o) onClose(); }}
            isDirty={isDirty}
            contentClassName="sm:max-w-lg max-h-[90vh] overflow-y-auto"
            testId="convert-dialog"
            title={<span className="flex items-center gap-2"><Lightning size={18} weight="duotone" className="text-amber-600" />Convert to proposal</span>}
            description={<>A Draft proposal will be created for <strong>{lead.company}</strong>. Client record auto-created if needed. Lead moves to "Proposal" stage.</>}
        >
                <div className="space-y-3">
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Template (optional)</Label>
                        <select
                            value={form.template_id}
                            onChange={(e) => onTemplateChange(e.target.value)}
                            className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white"
                            data-testid="convert-template"
                        >
                            <option value="">— don't apply a template —</option>
                            {templates.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}{(t.line_items?.length || 0) ? ` · ${t.line_items.length} items` : ""}</option>
                            ))}
                        </select>
                        {form.template_id && (
                            <div className="text-[11px] text-emerald-700 mt-1">
                                ✓ Header text + line items will be hydrated from this template.
                            </div>
                        )}
                    </div>
                    {/* Iter 66.12 — Module code field removed from Convert
                        dialog per operator feedback. Backend auto-derives
                        from the chosen template (or defaults to "Custom").
                        Currency now spans the full row. */}
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Currency</Label>
                        <select
                            value={form.currency}
                            onChange={(e) => setForm({ ...form, currency: e.target.value })}
                            className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white"
                            data-testid="convert-currency"
                        >
                            {["INR", "USD", "AED", "GBP", "EUR"].map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Kind Attention</Label>
                            <Input
                                value={form.kind_attention}
                                onChange={(e) => setForm({ ...form, kind_attention: e.target.value })}
                                className="mt-1 h-9 text-sm"
                                placeholder="Recipient name"
                                data-testid="convert-kind-attention"
                            />
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Proposal # (optional)</Label>
                            <Input
                                value={form.proposal_number}
                                onChange={(e) => setForm({ ...form, proposal_number: e.target.value })}
                                className="mt-1 h-9 text-sm font-mono"
                                placeholder="Auto-generated if blank"
                                data-testid="convert-proposal-number"
                            />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Header text</Label>
                        <Input value={form.header_text} onChange={(e) => setForm({ ...form, header_text: e.target.value })} className="mt-1 h-9 text-sm" data-testid="convert-header-text" />
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Remarks</Label>
                        <textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded resize-none" data-testid="convert-remarks" />
                    </div>
                </div>
                <DialogFooter>
                    <FormDialogCancel variant="ghost" testId="convert-cancel">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={busy} data-testid="convert-submit" className="gap-1.5 bg-amber-600 hover:bg-amber-700">
                        <Lightning size={13} weight="fill" /> {busy ? "Converting…" : "Convert"}
                    </Button>
                </DialogFooter>
        </FormDialog>
    );
}
